import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, TrendingUp, Clock, Activity, AlertTriangle,
  Download, Calendar, RefreshCw, BarChart3, PieChart, LineChart
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import {
  LineChart as RechartsLineChart, Line, BarChart as RechartsBarChart, Bar,
  PieChart as RechartsPieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AnalyticsDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30); // days
  const [trendPeriod, setTrendPeriod] = useState('daily');
  
  // Analytics data
  const [overview, setOverview] = useState({});
  const [slaData, setSlaData] = useState([]);
  const [mttrData, setMttrData] = useState({});
  const [trendsData, setTrendsData] = useState([]);
  const [downtimeData, setDowntimeData] = useState({});

  useEffect(() => {
    fetchAllAnalytics();
  }, [period, trendPeriod]);

  const fetchAllAnalytics = async () => {
    setLoading(true);
    const token = localStorage.getItem('monitoringAdminToken');
    if (!token) {
      navigate('/monitoring-dashboard/login');
      return;
    }

    try {
      const headers = { 'Authorization': `Bearer ${token}` };

      // Fetch all analytics data in parallel
      const [overviewRes, slaRes, mttrRes, trendsRes, downtimeRes] = await Promise.all([
        fetch(`${API_URL}/api/monitoring/analytics/overview?days=${period}`, { headers }),
        fetch(`${API_URL}/api/monitoring/analytics/sla?days=${period}`, { headers }),
        fetch(`${API_URL}/api/monitoring/analytics/mttr?days=${period}`, { headers }),
        fetch(`${API_URL}/api/monitoring/analytics/trends?period=${trendPeriod}&days=${period}`, { headers }),
        fetch(`${API_URL}/api/monitoring/analytics/downtime?days=${period}`, { headers })
      ]);

      if (overviewRes.ok) setOverview(await overviewRes.json());
      if (slaRes.ok) setSlaData(await slaRes.json());
      if (mttrRes.ok) setMttrData(await mttrRes.json());
      if (trendsRes.ok) setTrendsData(await trendsRes.json());
      if (downtimeRes.ok) setDowntimeData(await downtimeRes.json());

      setLoading(false);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics data');
      setLoading(false);
    }
  };

  const COLORS = ['#ef4444', '#f97316', '#eab308', '#84cc16', '#22c55e'];

  const getSLAColor = (sla) => {
    if (sla >= 99.9) return '#22c55e'; // green
    if (sla >= 99) return '#84cc16'; // light green
    if (sla >= 95) return '#eab308'; // yellow
    if (sla >= 90) return '#f97316'; // orange
    return '#ef4444'; // red
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/monitoring-dashboard')}
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              {/* Netix Logo */}
              <div className="bg-white rounded-lg p-2 shadow-md">
                <img 
                  src="/netix-logo-horizontal.png" 
                  alt="Netix Fibernet" 
                  className="h-8 w-auto"
                />
              </div>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <BarChart3 className="h-6 w-6" />
                  Analytics Dashboard
                </h1>
                <p className="text-sm text-white/80">SLA tracking, MTTR metrics, and performance insights</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={period}
                onChange={(e) => setPeriod(Number(e.target.value))}
                className="px-3 py-1 rounded bg-white/20 text-white border border-white/30"
              >
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
              </select>
              <Button
                onClick={fetchAllAnalytics}
                size="sm"
                className="bg-white text-indigo-600 hover:bg-indigo-50"
              >
                <RefreshCw className="h-4 w-4 mr-1" />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="h-12 w-12 animate-spin mx-auto text-indigo-600 mb-4" />
            <p className="text-gray-600">Loading analytics...</p>
          </div>
        ) : (
          <>
            {/* Overview Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Average SLA</p>
                      <p className="text-3xl font-bold text-green-600">{overview.average_sla?.toFixed(2)}%</p>
                    </div>
                    <TrendingUp className="h-10 w-10 text-green-400" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Avg MTTR</p>
                      <p className="text-3xl font-bold text-blue-600">{mttrData.mttr_hours?.toFixed(1)}h</p>
                      <p className="text-xs text-gray-500">{mttrData.mttr_minutes?.toFixed(0)} minutes</p>
                    </div>
                    <Clock className="h-10 w-10 text-blue-400" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Downtime</p>
                      <p className="text-3xl font-bold text-red-600">{downtimeData.total_downtime_hours?.toFixed(1)}h</p>
                      <p className="text-xs text-gray-500">{downtimeData.total_alerts || 0} alerts</p>
                    </div>
                    <AlertTriangle className="h-10 w-10 text-red-400" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Active Customers</p>
                      <p className="text-3xl font-bold text-indigo-600">{overview.active_customers || 0}</p>
                      <p className="text-xs text-gray-500">of {overview.total_customers || 0} total</p>
                    </div>
                    <Activity className="h-10 w-10 text-indigo-400" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Alert Trends Chart */}
            <Card className="mb-6">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <LineChart className="h-5 w-5" />
                    Alert Trends
                  </CardTitle>
                  <div className="flex gap-2">
                    {['daily', 'weekly', 'monthly'].map((p) => (
                      <Button
                        key={p}
                        size="sm"
                        variant={trendPeriod === p ? 'default' : 'outline'}
                        onClick={() => setTrendPeriod(p)}
                        className="capitalize"
                      >
                        {p}
                      </Button>
                    ))}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsLineChart data={trendsData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="down_alerts" stroke="#ef4444" name="Down Alerts" strokeWidth={2} />
                    <Line type="monotone" dataKey="recovered_alerts" stroke="#22c55e" name="Recovered" strokeWidth={2} />
                    <Line type="monotone" dataKey="total_alerts" stroke="#6366f1" name="Total Alerts" strokeWidth={2} />
                  </RechartsLineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* SLA by Customer */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PieChart className="h-5 w-5" />
                    Customer SLA Performance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {slaData.slice(0, 10).map((customer, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <p className="font-semibold text-sm">{customer.office_name}</p>
                          <p className="text-xs text-gray-500">{customer.static_ip}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="text-sm font-bold" style={{ color: getSLAColor(customer.sla_percentage) }}>
                              {customer.sla_percentage.toFixed(2)}%
                            </p>
                            <p className="text-xs text-gray-500">{customer.alert_count} alerts</p>
                          </div>
                          <div className="w-24 bg-gray-200 rounded-full h-2">
                            <div
                              className="h-2 rounded-full"
                              style={{
                                width: `${customer.sla_percentage}%`,
                                backgroundColor: getSLAColor(customer.sla_percentage)
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Top Affected Customers */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5" />
                    Most Affected Customers
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {downtimeData.top_affected_customers?.slice(0, 10).map((customer, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                        <div className="flex-1">
                          <p className="font-semibold text-sm">{customer.office_name}</p>
                          <p className="text-xs text-gray-500">{customer.static_ip}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-red-600">{customer.total_downtime_hours.toFixed(1)}h</p>
                          <p className="text-xs text-gray-600">{customer.alert_count} alerts</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* MTTR Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Recovery Time Metrics (MTTR)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Average Recovery</p>
                    <p className="text-2xl font-bold text-blue-600">{mttrData.mttr_hours?.toFixed(2)}h</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Fastest Recovery</p>
                    <p className="text-2xl font-bold text-green-600">{mttrData.fastest_recovery_minutes?.toFixed(0)}m</p>
                  </div>
                  <div className="p-4 bg-red-50 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Slowest Recovery</p>
                    <p className="text-2xl font-bold text-red-600">{mttrData.slowest_recovery_minutes?.toFixed(0)}m</p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Median Recovery</p>
                    <p className="text-2xl font-bold text-purple-600">{mttrData.median_recovery_minutes?.toFixed(0)}m</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
